import React from 'react'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import RadioGroup from '@mui/material/RadioGroup'
import Radio from '@mui/material/Radio'
import Button from '@mui/material/Button/Button'
import FormHelperText from '@mui/material/FormHelperText'

import { useData, useHandlers } from './hooks'
import { FormControlLabel } from '@user/components/Scheduling/atoms/FormControlLabel.styled'
import { NoteInputField } from './NoteInputField'
import { EntityLocationDTO } from '@user/store/map/typings'
import { KnockStatusLocationBaseInput } from '@user/store/knockStatus'

type Props = {
  onClose: (hasExistingHistory?: boolean) => void
  reload?: () => void
  hasExistingHistory: boolean
  currentKnockStatusName?: string | null
  onRefresh?: () => void
  address: EntityLocationDTO | null
  onSaveNewKnockStatusLocation?: (
    newKnockLocation: KnockStatusLocationBaseInput,
  ) => void
}

export function AddKnockStatusForm({
  onClose,
  reload,
  hasExistingHistory,
  currentKnockStatusName,
  onRefresh,
  address,
  onSaveNewKnockStatusLocation,
}: Props) {
  const { allStatuses, initialValues } = useData()
  const { onSubmit } = useHandlers({
    onClose,
    reload,
    hasExistingHistory,
    currentKnockStatusName,
    onRefresh,
    address,
    onSaveNewKnockStatusLocation,
  })

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={Yup.object({
        knockStatus: Yup.string().required('Knock status is required'),
        note: Yup.string()
          .nullable()
          .max(200, 'Reason must be 200 characters or less'),
      })}
      onSubmit={onSubmit}
    >
      {({
        isSubmitting,
        values,
        resetForm,
        setFieldValue,
        errors,
        touched,
      }) => (
        <Form noValidate autoComplete="off">
          <Box>
            <Typography variant="body1">Select knock status*</Typography>
            <RadioGroup
              name="knockStatus"
              value={values.knockStatus}
              onChange={(e) => setFieldValue('knockStatus', e.target.value)}
            >
              {allStatuses.map((status) => (
                <FormControlLabel
                  disabled={
                    isSubmitting || status.name === currentKnockStatusName
                  }
                  key={status.id}
                  control={
                    <Radio
                      sx={{ color: (theme) => theme.palette.secondary.main }}
                    />
                  }
                  value={status.name}
                  label={status.name}
                />
              ))}
            </RadioGroup>

            {touched.knockStatus && !!errors.knockStatus && (
              <FormHelperText error required>
                Required
              </FormHelperText>
            )}
          </Box>
          <NoteInputField />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
              mt: 0.8,
              mb: 3.2,
            }}
          >
            <Button
              variant="text"
              type="button"
              onClick={() => {
                resetForm()
              }}
              disabled={
                isSubmitting ||
                (values.knockStatus === initialValues.knockStatus &&
                  values.note === initialValues.note)
              }
            >
              Clear
            </Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              Save
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  )
}
